import { Editor, type Node, Element as SlateElement, Transforms } from 'slate'

import { isBlockActive } from './block'
import { ACCEPTED_FILE_TYPES } from './constants'

// This is adapted from
// https://github.com/ianstormtaylor/slate/blob/main/site/examples/images.tsx

function isImageNode(n: Node): n is SlateElement & { type: 'image' } {
  return SlateElement.isElement(n) && n.type === 'image'
}

export function getActiveImageWidth(editor: Editor): number | null {
  const { selection } = editor
  if (selection == null) {
    return null
  }
  const [link] = Editor.nodes(editor, {
    at: Editor.unhangRange(editor, selection),
    match: isImageNode,
  })
  if (link == null) {
    return null
  }
  const [imageNode] = link
  return isImageNode(imageNode) ? (imageNode.width ?? null) : null
}

export function setImageWidth(editor: Editor, width: number | null): void {
  Transforms.setNodes(
    editor,
    {
      width,
    },
    { match: isImageNode }
  )
}

export function removeImage(editor: Editor): void {
  Transforms.removeNodes(editor, { match: isImageNode })
}

export function isImageActive(editor: Editor): boolean {
  return isBlockActive(editor, 'image')
}

export function insertImage(editor: Editor, url: string): void {
  const image: SlateElement = {
    type: 'image',
    url,
    children: [{ text: '' }],
  }
  Transforms.insertNodes(editor, image)
}

export function withImages(
  editor: Editor,
  params: { onFilesUpload?: (files: File[]) => void }
): Editor {
  const { insertData, isVoid } = editor

  // eslint-disable-next-line no-param-reassign
  editor.isVoid = element => (element.type === 'image' ? true : isVoid(element))

  // eslint-disable-next-line no-param-reassign
  editor.insertData = data => {
    const { files } = data

    const acceptedFiles = Array.from(files).filter(file => ACCEPTED_FILE_TYPES.includes(file.type))

    if (acceptedFiles.length > 0) {
      params.onFilesUpload?.(acceptedFiles)
    } else {
      insertData(data)
    }
  }

  return editor
}
